import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"

export interface AmbientOcclusionSockets extends MaterialNodeSockets {
    inputs: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        distance: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    settings: {
        ao: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        distance: "float" // found in inputs: -; found in settings: float; inferred from data: float
        inside: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        normal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
        onlyLocal: "boolean" // found in inputs: -; found in settings: bool; inferred from data: boolean
        samples: "float"
    }
    outputs: {
        color: "color"
        ao: "float"
    }
}

export const ambientOcclusionDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeAmbientOcclusion,
    },
    nodeClass: "AmbientOcclusion",
    validation: {
        returns: z.strictObject({
            color: materialColorOutput,
            ao: materialFloatOutput,
        }),
        parameters: z.strictObject({
            inputs: z.strictObject({
                color: optionalMaterialInput,
                distance: optionalMaterialInput,
                normal: optionalMaterialInput,
            }),
            settings: z.strictObject({
                ao: z.optional(z.number()),
                color: z.optional(color),
                distance: z.optional(z.number()),
                inside: z.optional(z.boolean()),
                normal: z.optional(vec3),
                onlyLocal: z.optional(z.boolean()),
                samples: z.optional(z.number()),
            }),
            ui: materialUiSchema,
        }),
    },
    ui: {
        label: "Ambient Occlusion",
        color: "#2d8a5d",
        items: [
            {name: "samples", valueType: "float", label: "Samples", input: false, setting: true, defaultValue: 16},
            // Outputs
            {name: "color", label: "Color", valueType: "color", output: true},
            {name: "ao", label: "AO", valueType: "float", output: true},

            // Inputs
            {name: "color", label: "Color", valueType: "color", input: true, setting: true, defaultValue: {r: 1, g: 1, b: 1}},
            {name: "distance", label: "Distance", valueType: "float", input: true, setting: true, defaultValue: 1},
            {name: "normal", label: "Normal", valueType: "normal", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            {name: "inside", label: "Inside", valueType: "boolean", defaultValue: false, input: false, setting: true},
            {name: "onlyLocal", label: "Only Local", valueType: "boolean", defaultValue: false, input: false, setting: true},
        ],
        support: {
            cycles: true,
            three: false,
        },
    },
} satisfies MaterialNodeDefinition<AmbientOcclusionSockets>
